import {
  createApi,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { RecordResponse } from '@/models/ServiceTypes';
import { Role } from '@/models/AccountEnums';
import type {
  HomeAwayInstitution,
  HomeAwayInstitutionsRequest,
} from '@/modules/dataFeeds/types';
import { waServicesQuery } from './baseQueries';
import { dataFeedsApi } from './dataFeedsApi';
import type { WARootState } from './store';

export const homeAwayInstitutionsApi = createApi({
  reducerPath: 'homeAwayInstitutionsApi',
  baseQuery: waServicesQuery,
  tagTypes: ['HomeAwayInstitutions'],
  endpoints: (builder) => ({
    getHomeAwayInstitutions: builder.query<
      RecordResponse<HomeAwayInstitution>,
      HomeAwayInstitutionsRequest
    >({
      queryFn: async (args, { getState }, _extraOptions, baseQuery) => {
        const state = getState() as WARootState;
        if (state.auth.currentRole !== Role.Firm) {
          return {
            error: {
              status: 403,
              data: 'Home/away institutions are only available for firm role',
            } as FetchBaseQueryError,
          };
        }

        const result = await baseQuery({
          url: '/api/firm/homeAwayInstitutions',
          method: 'GET',
          params: args,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as RecordResponse<HomeAwayInstitution> };
      },
      providesTags: (_result, _error, args) => [
        {
          type: 'HomeAwayInstitutions',
          id: args.OnlyHome === true ? 'HOME' : 'AWAY',
        },
      ],
    }),
    updateHomeAwayInstitution: builder.mutation<
      boolean,
      { financialInstitutionId: number; body: HomeAwayInstitution }
    >({
      queryFn: async (
        { financialInstitutionId, body },
        { getState, dispatch },
        _extraOptions,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        if (state.auth.currentRole !== Role.Firm) {
          return {
            error: {
              status: 403,
              data: 'Home/away institutions are only available for firm role',
            } as FetchBaseQueryError,
          };
        }

        const result = await baseQuery({
          url: `/api/firm/homeAwayInstitutions/${String(financialInstitutionId)}`,
          method: 'PUT',
          body,
        });

        if (result.error) {
          return { error: result.error };
        }

        // Non-converted firms read the feeds list from dataFeedsApi.getHomeInstitutions.
        dispatch(
          dataFeedsApi.util.invalidateTags([
            { type: 'HomeInstitutions', id: 'LIST' },
          ]),
        );

        return { data: result.data as boolean };
      },
      invalidatesTags: [
        { type: 'HomeAwayInstitutions', id: 'HOME' },
        { type: 'HomeAwayInstitutions', id: 'AWAY' },
      ],
    }),
  }),
});

export const {
  useGetHomeAwayInstitutionsQuery,
  useLazyGetHomeAwayInstitutionsQuery,
  useUpdateHomeAwayInstitutionMutation,
} = homeAwayInstitutionsApi;
